import type { Environment } from '../../contexts/AppContext';
import { apiConfig } from '../../config/api';

const getAPIBaseURL = (): string => {
  if (apiConfig.error) {
    throw new Error(apiConfig.error);
  }

  return apiConfig.apiBaseUrl;
};

const DEFAULT_PARTITION_KEY = 'ADMINCI';

export interface TRAPIClientConfig {
  environment: Environment;
  accessToken?: string;
  partitionKey?: string;
}

export class TRAPIClient {
  private environment: Environment;
  private partitionKey: string;

  constructor(config: TRAPIClientConfig) {
    this.environment = config.environment;
    this.partitionKey = config.partitionKey || DEFAULT_PARTITION_KEY;
  }

  setEnvironment(environment: Environment) {
    this.environment = environment;
  }

  setPartitionKey(partitionKey: string) {
    this.partitionKey = partitionKey;
  }

  async request<T = unknown>(config: { url?: string }, accessToken?: string): Promise<T> {
    // Not used in this trimmed client
    const response = await this.get<T>(config.url || '/', accessToken);
    return response;
  }

  async get<T = unknown>(url: string, accessToken?: string): Promise<T> {
    // Checkpoint df4e98f8 review finding ARCH-3: delegate to getWithHeaders()
    // rather than re-implementing the same fetch/error/JSON-parse logic a
    // second time -- a fix to one no longer risks silently diverging from
    // the other for the same HTTP verb.
    const { data } = await this.getWithHeaders<T>(url, accessToken);
    return data;
  }

  /**
   * Same as get(), but (a) accepts extra request headers -- e.g. an explicit
   * `trapi-partitionkey` pin, rather than relying on the server's default --
   * and (b) returns the raw response Headers alongside the parsed body, so a
   * caller can inspect response headers the base get() discards (e.g. a
   * future pagination/continuation contract on a currently-unpaginated list
   * endpoint).
   */
  async getWithHeaders<T = unknown>(
    url: string,
    accessToken?: string,
    extraHeaders?: Record<string, string>,
  ): Promise<{ data: T; headers: Headers }> {
    const fullUrl = `${getAPIBaseURL()}${url}`;
    const headers: Record<string, string> = { ...(extraHeaders || {}) };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const res = await fetch(fullUrl, { method: 'GET', headers, mode: 'cors', credentials: 'omit' });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${res.statusText} - ${text}`);
    }
    const data = (await res.json()) as T;
    return { data, headers: res.headers };
  }

  async post<T = unknown>(url: string, data?: unknown, accessToken?: string): Promise<T> {
    const fullUrl = `${getAPIBaseURL()}${url}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'trapi-partitionkey': this.partitionKey,
    };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch(fullUrl, { method: 'POST', headers, body: JSON.stringify(data), mode: 'cors', credentials: 'omit' });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${res.statusText} - ${text}`);
    }
    // Handle empty/204 responses gracefully
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    const contentLength = res.headers.get('content-length');
    const contentType = res.headers.get('content-type') || '';
    if (contentLength === '0') {
      return undefined as unknown as T;
    }
    const text = await res.text();
    if (!text || text.trim().length === 0) {
      return undefined as unknown as T;
    }
    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }
    // Fallback: return raw text if not JSON
    return text as unknown as T;
  }

  async put<T = unknown>(url: string, data?: unknown, accessToken?: string): Promise<T> {
    const fullUrl = `${getAPIBaseURL()}${url}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'trapi-partitionkey': this.partitionKey,
    };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    
    let body: string;
    try {
      body = JSON.stringify(data);
    } catch (err) {
      console.error('Failed to stringify payload:', err);
      throw new Error(`Failed to serialize request body: ${err instanceof Error ? err.message : String(err)}`);
    }
    
    const res = await fetch(fullUrl, { method: 'PUT', headers, body, mode: 'cors', credentials: 'omit' });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${res.statusText} - ${text}`);
    }
    // Handle empty/204 responses gracefully
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    const contentLength = res.headers.get('content-length');
    const contentType = res.headers.get('content-type') || '';
    if (contentLength === '0') {
      return undefined as unknown as T;
    }
    const text = await res.text();
    if (!text || text.trim().length === 0) {
      return undefined as unknown as T;
    }
    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }
    // Fallback: return raw text if not JSON
    return text as unknown as T;
  }

  async delete<T = unknown>(url: string, accessToken?: string): Promise<T> {
    const fullUrl = `${getAPIBaseURL()}${url}`;
    const headers: Record<string, string> = { 'trapi-partitionkey': this.partitionKey };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch(fullUrl, { method: 'DELETE', headers, mode: 'cors', credentials: 'omit' });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${res.statusText} - ${text}`);
    }
    // Handle empty/204 responses gracefully
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    const contentLength = res.headers.get('content-length');
    const contentType = res.headers.get('content-type') || '';
    if (contentLength === '0') {
      return undefined as unknown as T;
    }
    const text = await res.text();
    if (!text || text.trim().length === 0) {
      return undefined as unknown as T;
    }
    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }
    // Fallback: return raw text if not JSON
    return text as unknown as T;
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  getBaseURL(): string {
    return getAPIBaseURL();
  }

  getPartitionKey(): string {
    return this.partitionKey;
  }
}

export const createTRAPIClient = (config: TRAPIClientConfig): TRAPIClient => new TRAPIClient(config);
export let trapiClient: TRAPIClient;
export const initializeTRAPIClient = (config: TRAPIClientConfig) => {
  trapiClient = createTRAPIClient(config);
  return trapiClient;
};

// ---------------------------------------------------------------------------
// Generic opt-in /configs pagination support (AB#273140, PR6 review F2).
//
// Checkpoint df4e98f8 review finding ARCH-1: this was originally written
// scoped to and named after MODEL_APPROVAL inside modelApprovalsService.ts.
// It has no MODEL_APPROVAL-specific logic at all -- it is a generic
// pagination-following helper for ANY `/configs`-backed list endpoint that
// opts in to the same `trapi-max-item-count` / `trapi-continuation` /
// `trapi-pagination` contract (e.g. ApisService.getApis, which today has no
// page limit either). Living here, next to getWithHeaders() (the method it
// is built on), means a second consumer becomes a caller of this file rather
// than either importing a misleadingly-named MODEL_APPROVAL-specific error
// class or re-copying the ~70-line pagination-following loop into another
// service file.
//
// Contract (verified live in DEV against GET /configs?ci=MODEL_APPROVAL with
// ADMINCI): pagination is opt-in -- a caller that sends `trapi-max-item-count`
// asks the server to page results. Every response to such a request MUST
// echo `trapi-pagination: supported`; a missing/other value means the server
// did not honor the opt-in and the response cannot be trusted to be
// paginated at all, so callers of fetchAllPaginatedConfigs fail closed rather
// than assuming a full result set. `trapi-continuation` is the ONLY
// continuation signal understood here: when the response carries a
// non-empty value, more pages remain and that exact token must be echoed
// back as the request's `trapi-continuation` header on the next call; an
// empty/absent value means this was the last page. The server's raw
// Cosmos-level `x-ms-continuation` header is a distinct, lower-level signal
// this helper does not page through -- if it is ever non-empty, a page may
// have been truncated in a way this contract does not describe, so this
// helper fails closed rather than silently under-reporting the list.
//
// IMPORTANT (per-branch caveat): the exact server-side get-configs.xml
// implementation of this contract is owned by a separate, out-of-scope
// change (informally "PR4") and may not be reflected verbatim in every
// branch of this repository at any given time -- see the git history/PR
// description for this file for the live-DEV verification evidence this
// contract is based on.
//
// Single-page fallback (PR6 hosted-review finding, thread 971361): a
// get-configs.xml that PREDATES this opt-in contract entirely responds to
// the `trapi-max-item-count` opt-in with a bare 200 array and simply omits
// `trapi-pagination` -- it never claimed to support paging, so there is
// nothing to distrust about that one response; Cosmos cross-partition
// queries issued without an explicit page size return the FULL result set
// in one page in that case, so the bare array already IS the complete list.
// Treating that specific shape as a fatal error (rather than the complete
// answer it is) was the exact bug this finding identified: PR6's own admin
// Model Approvals page would fail closed on every single load against
// today's get-configs.xml, well before "PR4" merges. This is therefore a
// NARROW degrade, not "absent header == ok": it only applies when the
// capability header is absent on the very FIRST page AND the server did not
// also offer a continuation token on that same response -- neither the
// trapi-continuation opt-in signal NOR the raw Cosmos-level legacy
// x-ms-continuation header, since either one, offered alongside an absent
// capability header, would be self-contradictory (a server cannot decline
// the pagination contract and still hand out a token for it, whichever
// header carries that token). An absent header on any LATER page (after
// an earlier page already confirmed `trapi-pagination: supported` and
// handed out a continuation token), a PRESENT-but-wrong capability value,
// a non-empty legacy `x-ms-continuation`, a non-array body, a repeated
// (no-progress) continuation token, and the page-count ceiling all
// continue to fail closed exactly as before -- none of those are the
// "server never implemented this contract" shape, they are signs of a
// genuinely inconsistent or truncated paginated response and must still be
// distrusted rather than silently accepted.
export const MAX_ITEM_COUNT_REQUEST_HEADER = 'trapi-max-item-count';
export const CONTINUATION_HEADER = 'trapi-continuation';
export const PAGINATION_CAPABILITY_HEADER = 'trapi-pagination';
export const PAGINATION_CAPABILITY_SUPPORTED = 'supported';
export const LEGACY_CONTINUATION_HEADER = 'x-ms-continuation';

// A conservative ceiling on the number of pages fetchAllPaginatedConfigs will
// follow before refusing to continue, bounding a runaway loop if a server
// defect never stops emitting continuation tokens. Callers pass their own
// page size; at the 1000-row page size used by ModelApprovalsService, this
// allows up to 50,000 records -- far beyond any plausible /configs list
// volume today.
export const DEFAULT_MAX_PAGES = 50;

/**
 * Thrown when a paginated `/configs` list cannot be proven complete: the
 * opt-in pagination contract (see above) was violated in some concrete,
 * detectable way. `resourceDescription` identifies which list this was
 * (e.g. "model approval"), and `detail` describes the SPECIFIC violation
 * observed -- a required capability header was absent or wrong, the
 * continuation token made no progress between two requests, an unexpected
 * legacy Cosmos-level continuation signal appeared, a page body was not the
 * expected array shape, or the page-count ceiling was reached without the
 * server signaling completion. This is deliberately not a single generic
 * message, because "some signal was present" conflates the pagination
 * *capability* signal with an actual *incompleteness* signal -- the exact
 * bug this contract exists to avoid repeating. Failing closed here -- rather
 * than silently presenting a partial or unverifiable list as complete -- is
 * deliberate: a caller acting on an incomplete list (e.g. an admin
 * approving/rejecting a model) is worse off than seeing an error that says
 * so.
 */
export class IncompletePaginatedListError extends Error {
  override name = 'IncompletePaginatedListError';
  constructor(resourceDescription: string, detail: string) {
    super(
      `The ${resourceDescription} list could not be confirmed complete: ${detail}. ` +
      'Reload, or contact TRAPI admins if this persists.',
    );
  }
}

/** One page's worth of raw fetch result, matching TRAPIClient.getWithHeaders. */
export interface RawConfigPage {
  data: unknown;
  headers: Headers;
}

/**
 * Pure, dependency-free pagination-following helper for any `/configs`-
 * backed list endpoint that opts in to the contract documented above. Takes
 * a `fetchPage` callback -- so it can be exercised with a canned mock
 * returning {data, headers} with no real fetch/DOM involved -- and follows
 * `trapi-continuation` until the server reports no more pages, validating
 * the capability / legacy-signal / no-progress / shape / page-count
 * invariants on every single page, not just the first.
 *
 * `fetchPage` is called with the continuation token to send (`undefined`
 * for the first page) and the page size to request, and must return the raw
 * page result exactly as `TRAPIClient.getWithHeaders` would.
 *
 * `resourceDescription` is a short, human-readable name for what is being
 * listed (e.g. "model approval"), used only in the thrown error's message.
 */
export async function fetchAllPaginatedConfigs<T>(
  fetchPage: (continuationToken: string | undefined, pageSize: number) => Promise<RawConfigPage>,
  resourceDescription: string,
  pageSize: number,
  maxPages: number = DEFAULT_MAX_PAGES,
): Promise<T[]> {
  const items: T[] = [];
  let continuationToken: string | undefined;
  let pageIndex = 0;

  do {
    pageIndex += 1;
    if (pageIndex > maxPages) {
      throw new IncompletePaginatedListError(
        resourceDescription,
        `exceeded the ${maxPages}-page safety limit without the server signaling completion`,
      );
    }

    const { data, headers } = await fetchPage(continuationToken, pageSize);

    if (!Array.isArray(data)) {
      throw new IncompletePaginatedListError(resourceDescription, 'the server returned a non-array page body');
    }

    // Read once, side-effect-free, so the capability check below can decide
    // the single-page-fallback question (does this response ALSO fail to
    // offer a continuation token?) without a second header lookup. The
    // legacy Cosmos-level header is read here too (rather than only in its
    // own check further below) for the same reason: the fallback must not
    // fire if a raw x-ms-continuation is present, even though trapi-
    // continuation is absent -- otherwise the fallback would silently
    // discard a truncation signal it explicitly claims to still honor.
    const nextContinuation = headers.get(CONTINUATION_HEADER);
    const legacyContinuation = headers.get(LEGACY_CONTINUATION_HEADER);

    if (legacyContinuation) {
      throw new IncompletePaginatedListError(
        resourceDescription,
        `an unexpected legacy ${LEGACY_CONTINUATION_HEADER} signal was present; this client only follows ${CONTINUATION_HEADER}`,
      );
    }

    const capability = headers.get(PAGINATION_CAPABILITY_HEADER);
    if (capability !== PAGINATION_CAPABILITY_SUPPORTED) {
      // Documented single-page fallback (see the file-level comment above
      // this contract's constants): an ABSENT capability header on the very
      // first page, with no trapi-continuation OR legacy x-ms-continuation
      // offered either, means the server never opted into this contract at
      // all -- distinct from a present-but-wrong value, an absent header on
      // a later page, or an absent header paired with EITHER continuation
      // signal, all of which still fail closed below exactly as before.
      if (capability === null && pageIndex === 1 && !nextContinuation && !legacyContinuation) {
        return data as T[];
      }

      throw new IncompletePaginatedListError(
        resourceDescription,
        `expected ${PAGINATION_CAPABILITY_HEADER}: ${PAGINATION_CAPABILITY_SUPPORTED} on an opt-in paginated ` +
        `request (sent ${MAX_ITEM_COUNT_REQUEST_HEADER}), got ${capability === null ? '<absent>' : JSON.stringify(capability)}`,
      );
    }

    items.push(...(data as T[]));

    const previousContinuationToken = continuationToken;
    continuationToken = nextContinuation ? nextContinuation : undefined;

    if (continuationToken && continuationToken === previousContinuationToken) {
      throw new IncompletePaginatedListError(
        resourceDescription,
        'the server returned the same continuation token twice in a row (no progress)',
      );
    }
  } while (continuationToken);

  return items;
}

